import { PARTNER_LINK_TTL_MS as ATTRIBUTION_TTL_MS } from "./utmDatasCookie";

interface UserPreferences {
  user_email?: string;
  rememberMe: boolean;
  is_new_user: boolean;
  users_list_limit?: number;
  team_member_entity_id?: string;
}

interface PaymentFlowData {
  user_email: string;
  is_new_user: boolean;
  has_active_payment_flow: boolean;
}

interface AttributionData {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  gad_source?: string;
  bp_id?: string;
  bp_id_captured_at?: number;
  utm?: Record<string, string>;
  captured_at: number;
}

const ATTRIBUTION_ID_KEYS = [
  "gclid",
  "wbraid",
  "gbraid",
  "gad_source",
  "bp_id",
] as const;

export interface AuthService {
  user_identification?: string;
  access_token?: string;
  entity_identification?: string;
  selectedPlan?: string;
  password_validation_regex_level?: string;
  exp?: Date;
  rememberMe?: boolean;
  [key: string]: any;
}

const PROTECTED_KEYS = {
  UMD_SERVICE: "_umd_service",
  UMD_CREDENTIAL: "_umd_credential",
  UMD_OAUTH_CREDENTIAL: "_umd_oauth_credential",
} as const;

const STORAGE_KEYS = {
  USER_PREFERENCES: "_user_preferences",
  PAYMENT_FLOW: "_payment_flow_data",
  ATTRIBUTION: "_attribution_data",
} as const;

const IMPERSONATION_KEY = "sum:impersonation";

const IMPERSONATION_TAB_MARKER = "sum:is_impersonation_tab";

const CUSTOMER_SESSION_KEY = "sum:customer_session";

const CUSTOMER_TAB_MARKER = "sum:is_customer_tab";

const GENERIC_AUTH_STATE_KEY = "auth_state";

export class SecureStorage {
  static isImpersonating(): boolean {
    try {
      return sessionStorage.getItem(IMPERSONATION_KEY) !== null;
    } catch {
      return false;
    }
  }

  static getImpersonationSession(): AuthService | null {
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn("Error parsing impersonation session data:", error);
      return null;
    }
  }

  static isImpersonationTab(): boolean {
    try {
      return sessionStorage.getItem(IMPERSONATION_TAB_MARKER) !== null;
    } catch {
      return false;
    }
  }

  static setImpersonationSession(data: AuthService): void {
    try {
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data));
      sessionStorage.setItem(IMPERSONATION_TAB_MARKER, "1");
    } catch (error) {
      console.error("Error storing impersonation session data:", error);
    }
  }

  static clearImpersonationSession(): void {
    try {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch (error) {
      console.warn("Error clearing impersonation session data:", error);
    }
  }

  static isCustomerTab(): boolean {
    try {
      return sessionStorage.getItem(CUSTOMER_TAB_MARKER) !== null;
    } catch {
      return false;
    }
  }

  static markCustomerTab(): void {
    try {
      sessionStorage.setItem(CUSTOMER_TAB_MARKER, "1");
    } catch (error) {
      console.error("Error marking customer tab:", error);
    }
  }

  static hasCustomerSession(): boolean {
    try {
      return sessionStorage.getItem(CUSTOMER_SESSION_KEY) !== null;
    } catch {
      return false;
    }
  }

  static getCustomerSession(): AuthService | null {
    try {
      const stored = sessionStorage.getItem(CUSTOMER_SESSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn("Error parsing customer session data:", error);
      return null;
    }
  }

  static setCustomerSession(data: AuthService): void {
    try {
      sessionStorage.setItem(CUSTOMER_SESSION_KEY, JSON.stringify(data));
      sessionStorage.setItem(CUSTOMER_TAB_MARKER, "1");
    } catch (error) {
      console.error("Error storing customer session data:", error);
    }
  }

  static clearCustomerSession(): void {
    try {
      sessionStorage.removeItem(CUSTOMER_SESSION_KEY);
    } catch (error) {
      console.warn("Error clearing customer session data:", error);
    }
  }

  static invalidateCustomerSession(): void {
    try {
      const deadToken = this.getCustomerSession()?.access_token;
      this.clearCustomerSession();
      if (!deadToken) return;
      const rawAuthState = localStorage.getItem(GENERIC_AUTH_STATE_KEY);
      if (!rawAuthState) return;
      const mirrored = JSON.parse(rawAuthState);
      if (mirrored?.access_token === deadToken) {
        localStorage.removeItem(GENERIC_AUTH_STATE_KEY);
      }
    } catch (error) {
      console.warn("Error invalidating customer session:", error);
    }
  }

  static getAuthServiceData(): AuthService | null {
    const impersonation = this.getImpersonationSession();
    if (impersonation) {
      return impersonation;
    }
    if (this.isCustomerTab()) {
      return this.getCustomerSession();
    }
    try {
      const stored = localStorage.getItem(PROTECTED_KEYS.UMD_SERVICE);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn("Error parsing authentication service data:", error);
      return null;
    }
  }

  private static setAuthServiceData(data: AuthService): void {
    try {
      localStorage.setItem(PROTECTED_KEYS.UMD_SERVICE, JSON.stringify(data));
    } catch (error) {
      console.error("Error storing authentication service data:", error);
    }
  }

  static getAuthCredentials(): { username: string; password: string } | null {
    const authData = this.getAuthServiceData();
    if (!authData?.user_identification || !authData?.access_token) {
      return null;
    }

    return {
      username: authData.user_identification,
      password: authData.access_token,
    };
  }

  static isAuthenticated(): boolean {
    const authData = this.getAuthServiceData();
    return !!(authData?.user_identification && authData?.access_token);
  }

  static getUserIdentification(): string | null {
    const authData = this.getAuthServiceData();
    return authData?.user_identification || null;
  }

  static getEntityIdentification(): string | null {
    const authData = this.getAuthServiceData();
    return authData?.entity_identification || null;
  }

  static updateAuthData(newAuthData: Partial<AuthService>): void {
    if (this.isImpersonating()) {
      const existing = this.getImpersonationSession() || {};
      this.setImpersonationSession({ ...existing, ...newAuthData });
      return;
    }
    if (this.isCustomerTab()) {
      const existing = this.getCustomerSession() || {};
      this.setCustomerSession({ ...existing, ...newAuthData });
      return;
    }
    const existingData = this.getAuthServiceData() || {};
    const updatedData = { ...existingData, ...newAuthData };
    this.setAuthServiceData(updatedData);
  }

  static clearAuthData(): void {
    if (this.isImpersonating()) {
      this.clearImpersonationSession();
      return;
    }
    if (this.isCustomerTab()) {
      this.clearCustomerSession();
      return;
    }
    localStorage.removeItem(PROTECTED_KEYS.UMD_SERVICE);
    localStorage.removeItem(PROTECTED_KEYS.UMD_CREDENTIAL);
    localStorage.removeItem(PROTECTED_KEYS.UMD_OAUTH_CREDENTIAL);
  }

  static setUserPreferences(preferences: UserPreferences): void {
    localStorage.setItem(
      STORAGE_KEYS.USER_PREFERENCES,
      JSON.stringify(preferences)
    );
  }

  static getUserPreferences(): UserPreferences | null {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
    return stored ? JSON.parse(stored) : null;
  }

  static setTeamMemberEntityId(entityId: string): void {
    const current = SecureStorage.getUserPreferences() || {
      rememberMe: false,
      is_new_user: false,
    };
    SecureStorage.setUserPreferences({
      ...current,
      team_member_entity_id: entityId,
    });
  }

  static getTeamMemberEntityId(): string | null {
    return SecureStorage.getUserPreferences()?.team_member_entity_id || null;
  }

  static setPaymentFlowData(data: PaymentFlowData): void {
    localStorage.setItem(STORAGE_KEYS.PAYMENT_FLOW, JSON.stringify(data));
  }

  static getPaymentFlowData(): PaymentFlowData | null {
    const stored = localStorage.getItem(STORAGE_KEYS.PAYMENT_FLOW);
    return stored ? JSON.parse(stored) : null;
  }

  static hasActivePaymentFlow(): boolean {
    const data = this.getPaymentFlowData();
    return data?.has_active_payment_flow || false;
  }

  static clearPaymentFlowData(): void {
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_FLOW);
  }

  static clearUserPreferences(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
  }

  static setAttributionData(
    params: Partial<Omit<AttributionData, "captured_at">>
  ): void {
    const now = Date.now();
    const incoming: Partial<AttributionData> = {};
    for (const key of ATTRIBUTION_ID_KEYS) {
      const value = params[key];
      if (value) incoming[key] = value;
    }
    let incomingBpIdAt = now;
    if (incoming.bp_id && params.bp_id_captured_at !== undefined) {
      const at = params.bp_id_captured_at;
      if (
        typeof at === "number" &&
        Number.isFinite(at) &&
        at <= now &&
        now - at <= ATTRIBUTION_TTL_MS
      ) {
        incomingBpIdAt = at;
      } else {
        delete incoming.bp_id;
      }
    }
    const incomingUtm: Record<string, string> = {};
    if (params.utm) {
      for (const [key, value] of Object.entries(params.utm)) {
        if (value) incomingUtm[key] = value;
      }
    }

    if (
      Object.keys(incoming).length === 0 &&
      Object.keys(incomingUtm).length === 0
    ) {
      return;
    }

    try {
      const existing = this.getAttributionData();
      // A new set of UTM replaces the previous one as a whole: mixing two visits' fields would credit neither.
      const utm =
        Object.keys(incomingUtm).length > 0 ? incomingUtm : existing?.utm;
      const merged: AttributionData = {
        ...(existing || {}),
        ...incoming,
        ...(utm && Object.keys(utm).length > 0 ? { utm } : {}),
        captured_at: now,
      };
      if (incoming.bp_id) {
        merged.bp_id_captured_at = incomingBpIdAt;
      } else if (merged.bp_id && typeof merged.bp_id_captured_at !== "number") {
        merged.bp_id_captured_at = existing?.captured_at ?? now;
      }
      localStorage.setItem(STORAGE_KEYS.ATTRIBUTION, JSON.stringify(merged));
    } catch (error) {
      console.warn("Error storing attribution data:", error);
    }
  }

  static getAttributionData(): AttributionData | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ATTRIBUTION);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as AttributionData;
      if (
        !parsed ||
        typeof parsed.captured_at !== "number" ||
        Date.now() - parsed.captured_at > ATTRIBUTION_TTL_MS
      ) {
        return null;
      }
      const bpIdCapturedAt =
        typeof parsed.bp_id_captured_at === "number"
          ? parsed.bp_id_captured_at
          : parsed.captured_at;
      if (parsed.bp_id && Date.now() - bpIdCapturedAt > ATTRIBUTION_TTL_MS) {
        const withoutPartner: AttributionData = { ...parsed };
        delete withoutPartner.bp_id;
        delete withoutPartner.bp_id_captured_at;
        return withoutPartner;
      }
      return parsed;
    } catch (error) {
      console.warn("Error parsing attribution data:", error);
      return null;
    }
  }

  static clearAttributionData(): void {
    localStorage.removeItem(STORAGE_KEYS.ATTRIBUTION);
  }

  static clearAll(): void {
    this.clearPaymentFlowData();
    this.clearUserPreferences();
  }

  static setSelectedPlan(planName: string): void {
    this.updateAuthData({ selectedPlan: planName });
  }

  static getSelectedPlan(): string | null {
    const authData = this.getAuthServiceData();
    return authData?.selectedPlan || null;
  }

  static clearSelectedPlan(): void {
    if (this.isImpersonating()) {
      const session = this.getImpersonationSession();
      if (session) {
        delete session.selectedPlan;
        this.setImpersonationSession(session);
      }
      return;
    }
    if (this.isCustomerTab()) {
      const session = this.getCustomerSession();
      if (session) {
        delete session.selectedPlan;
        this.setCustomerSession(session);
      }
      return;
    }
    const authData = this.getAuthServiceData();
    if (authData) {
      delete authData.selectedPlan;
      this.setAuthServiceData(authData);
    }
  }

  static getPasswordValidationLevel(): string {
    const authData = this.getAuthServiceData();
    return authData?.password_validation_regex_level || "level2";
  }

  static setPasswordValidationLevel(level: string): void {
    this.updateAuthData({ password_validation_regex_level: level });
  }

  static getOAuthCredentials(): any | null {
    try {
      const stored = localStorage.getItem(PROTECTED_KEYS.UMD_OAUTH_CREDENTIAL);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn("Error parsing OAuth credentials:", error);
      return null;
    }
  }

  static setOAuthCredentials(credentials: any): void {
    try {
      localStorage.setItem(
        PROTECTED_KEYS.UMD_OAUTH_CREDENTIAL,
        JSON.stringify(credentials)
      );
    } catch (error) {
      console.error("Error storing OAuth credentials:", error);
    }
  }

  static clearOAuthCredentials(): void {
    localStorage.removeItem(PROTECTED_KEYS.UMD_OAUTH_CREDENTIAL);
  }

  static getUsersListLimit(): number {
    const legacyLimit = localStorage.getItem("umd_users_list_limit");
    if (legacyLimit && [10, 30, 50].includes(Number(legacyLimit))) {
      const limit = Number(legacyLimit);
      this.setUsersListLimit(limit);
      localStorage.removeItem("umd_users_list_limit");
      return limit;
    }

    const preferences = this.getUserPreferences();
    const limit = preferences?.users_list_limit;

    if (limit && [12, 30, 50].includes(limit)) {
      return limit;
    }

    return 12;
  }

  static setUsersListLimit(limit: number): void {
    if (![10, 30, 50].includes(limit)) {
      console.warn("Invalid users list limit. Must be 10, 30, or 50");
      return;
    }

    const existingPreferences = this.getUserPreferences() || {
      rememberMe: false,
      is_new_user: false,
    };

    this.setUserPreferences({
      ...existingPreferences,
      users_list_limit: limit,
    });
  }

  static removeInsecureStorage(): void {
    localStorage.removeItem(PROTECTED_KEYS.UMD_CREDENTIAL);

    const oldPlan = localStorage.getItem("_umd_service_client_plan");
    if (oldPlan) {
      this.setSelectedPlan(oldPlan);
      localStorage.removeItem("_umd_service_client_plan");
    }

    const oldLimit = localStorage.getItem("umd_users_list_limit");
    if (oldLimit && [10, 30, 50].includes(Number(oldLimit))) {
      this.setUsersListLimit(Number(oldLimit));
      localStorage.removeItem("umd_users_list_limit");
    }
  }
}

export default SecureStorage;
